import React from 'react';

/** Parse YYYY-MM-DD as local calendar date (avoid UTC day-shift) */
const parseLocalDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return Number.isNaN(d.getTime()) ? null : d;
  const s = String(d).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

export const fmtDate = (d) => {
  const dt = parseLocalDate(d);
  return dt
    ? dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
};

/**
 * R5-10: onboarding tasks are generated when the record is created, which can
 * be weeks before the person starts. Anything dated before their joining date
 * was therefore born overdue — "Send welcome email to new Joinee" showed red
 * for someone who does not arrive until next week.
 *
 * Nothing is hidden and no date is changed: the task and its date stay exactly
 * as they are. Only the overdue flag waits until the person has actually
 * joined, because until then nobody could have done the task anyway.
 *
 * The joining date is passed in rather than read off the task, since tasks
 * don't carry it. Without it the old behaviour applies, so any caller that
 * hasn't been updated keeps working.
 */
export const isTaskOverdue = (task, joiningDate = null) => {
  if (!task?.due_date || task.status === 'Done') return false;

  const joining = parseLocalDate(joiningDate ?? task.joining_date ?? null);
  if (joining) {
    joining.setHours(23, 59, 59, 999);
    if (joining >= new Date()) return false;
  }

  const due = parseLocalDate(task.due_date);
  if (!due) return false;
  due.setHours(23, 59, 59, 999);
  return due < new Date();
};

/** Ticket overdue when past due_date and not Resolved/Closed */
export const isTicketOverdue = (ticket) => {
  if (!ticket?.due_date || ['Resolved', 'Closed', 'Done'].includes(ticket.status)) return false;
  const due = parseLocalDate(ticket.due_date);
  if (!due) return false;
  due.setHours(23, 59, 59, 999);
  return due < new Date();
};

export const taskDueLabel = (task) => {
  if (task?.due_date) return `Due ${fmtDate(task.due_date)}`;
  if (task?.due_days != null && task.due_days !== 0) {
    return task.due_days > 0 ? `Day +${task.due_days}` : `Day ${task.due_days}`;
  }
  if (task?.due_days === 0) return 'Joining day';
  return null;
};

export const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const toInputDate = (d) => {
  const dt = parseLocalDate(d);
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export { parseLocalDate };

export const statusBadge = (s) => {
  const cls = (s || '').replace(' ', '-');
  return <span className={`badge badge-${cls}`}><span className="badge-dot"></span>{s}</span>;
};

export const priorityEl = (p) =>
  <span className={`priority-${p}`}>{p}</span>;

export const categoryChip = (c) => {
  const cls = { 'IT Support': 'IT', 'HR': 'HR', 'Admin': 'Admin', 'Facilities': 'Facilities' }[c] || 'IT';
  return <span className={`chip chip-${cls}`}>{c}</span>;
};

export const todayStr = () =>
  new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/**
 * A Date to YYYY-MM-DD in the calendar the person is actually living in.
 *
 * `toISOString().slice(0, 10)` looks like it does this and does not: it
 * converts to UTC first, so in IST every local midnight is 18:30 the previous
 * day and the string comes back one date early. That is what broke the Who Is
 * In arrows — stepping back a day from the 8th gave the 6th, and stepping
 * forward from the 6th gave the 6th again, because both landed either side of
 * the same UTC boundary.
 *
 * It is also why "today" was yesterday between midnight and 05:30.
 */
export const ymd = (d = new Date()) => {
  const dt = d instanceof Date ? d : parseLocalDate(d);
  if (!dt || Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** Today, as YYYY-MM-DD, locally. */
export const todayYmd = () => ymd(new Date());

/** N days from a YYYY-MM-DD string, still as YYYY-MM-DD, locally. */
export const addDays = (date, days) => {
  const dt = parseLocalDate(date);
  if (!dt) return '';
  dt.setDate(dt.getDate() + days);
  return ymd(dt);
};

export const timeGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

export const userInitials = (name) =>
  name?.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '??';

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#3b82f6,#8b5cf6)',
  'linear-gradient(135deg,#10b981,#06b6d4)',
  'linear-gradient(135deg,#f59e0b,#ef4444)',
  'linear-gradient(135deg,#8b5cf6,#ec4899)',
];

export const avatarGradient = (name) =>
  AVATAR_GRADIENTS[(name?.charCodeAt(0) || 0) % AVATAR_GRADIENTS.length];

export const uploadUrl = (filePath) => {
  if (!filePath) return '';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  // Form uploads may store `local:onboarding/...` when S3 is unavailable
  const cleaned = String(filePath).startsWith('local:')
    ? `uploads/${String(filePath).slice('local:'.length)}`
    : filePath;
  const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');
  return `${base}/${cleaned}`.replace(/([^:]\/)\/+/g, '$1');
};

export const isImageAttachment = (path) =>
  /^https?:\/\//i.test(path) || /\.(png|jpe?g|gif|webp)$/i.test(path) || String(path).includes('uploads/tickets');
